<script setup lang="ts">
import { format } from 'date-fns';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { abbreviate } from '../utils/utils';

export type MobilePhoneImage = {
    fileName: string;
    url: string;
    mimetype?: string;
};

const props = withDefaults(defineProps<{
    name: string;
    phoneNumber?: string | null;
    message: string;
    images?: MobilePhoneImage[];
    placeholder?: string;
}>(), {
    phoneNumber: null,
    images: () => [],
    placeholder: 'Your message will appear here.',
});

const currentTime = ref(new Date());

let timeInterval: number | null = null;

onMounted(() => {
    timeInterval = window.setInterval(() => {
        currentTime.value = new Date;
    }, 1000);
});

onUnmounted(() => {
    clearInterval(timeInterval ?? undefined);
});

const body = computed(() => props.message.trim());

function makeLinksClickable(text: string) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="break-words text-rose-300 underline hover:text-rose-200">$1</a>');
}

function escapeHtml(text: string) {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
}

// Merge tokens like {FirstName} are resolved by PBT at send time, so mark them
// in the preview rather than letting them read as literal copy.
function highlightMergeFields(text: string) {
    return text.replace(
        /\{\{?\s*[\w.]+\s*\}?\}/g,
        match => `<span class="rounded bg-rose-500/20 px-1 text-rose-200">${match}</span>`,
    );
}

// The message is author-supplied and echoed back through v-html, so escape
// first and only then introduce our own anchor and highlight markup.
const renderedMessage = computed(
    () => highlightMergeFields(makeLinksClickable(escapeHtml(body.value))),
);

function handleImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    if(target) {
        target.style.display = 'none';
    }
}
</script>

<template>
    <!-- Device frame -->
    <div class="relative w-[320px] h-[640px] rounded-[46px] bg-gradient-to-b from-neutral-700 to-neutral-900 p-2 shadow-xl ring-1 ring-neutral-900/10 dark:ring-white/10">
        <!-- Screen -->
        <div class="relative flex h-full flex-col overflow-hidden rounded-[38px] bg-neutral-950">
            <!-- Dynamic Island -->
            <div class="absolute top-2.5 left-1/2 z-10 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />

            <!-- Status bar -->
            <div class="flex items-center justify-between px-6 pt-3.5 pb-2 text-xs font-medium text-neutral-100">
                <div class="flex items-center gap-1">
                    <div class="size-1 rounded-full bg-neutral-100" />
                    <div class="size-1 rounded-full bg-neutral-100" />
                    <div class="size-1 rounded-full bg-neutral-100" />
                    <span class="ml-1.5 font-heading">AE&E</span>
                </div>
                <div class="flex items-center gap-1.5">
                    <span>100%</span>
                    <div class="h-2.5 w-5 rounded-sm border border-neutral-400 p-px">
                        <div class="h-full w-full rounded-[1px] bg-emerald-500" />
                    </div>
                </div>
            </div>

            <!-- Conversation header -->
            <div class="flex items-center gap-3 border-b border-neutral-800 bg-neutral-900/60 px-4 py-3 backdrop-blur-sm">
                <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose-400 to-rose-600">
                    <span class="text-xs font-semibold text-white">
                        {{ abbreviate(name, 2) }}
                    </span>
                </div>
                <div class="min-w-0">
                    <h3 class="truncate font-heading text-sm text-neutral-100">
                        {{ name }}
                    </h3>
                    <p
                        v-if="phoneNumber"
                        class="truncate text-xs text-neutral-400">
                        {{ phoneNumber }}
                    </p>
                </div>
            </div>

            <!-- Conversation -->
            <div class="flex-1 space-y-3 overflow-y-auto px-3 py-4">
                <div class="flex justify-start">
                    <div class="max-w-[85%] rounded-2xl rounded-bl-md bg-neutral-800 px-3 py-2 text-neutral-100">
                        <!-- eslint-disable vue/no-v-html -- renderedMessage escapes the
                        author's copy first and only then adds our own anchor and
                        merge-token markup. -->
                        <div
                            v-if="body"
                            class="text-sm break-words whitespace-pre-wrap"
                            v-html="renderedMessage" />
                        <!-- eslint-enable vue/no-v-html -->
                        <div
                            v-else
                            class="text-sm text-neutral-500 italic">
                            {{ placeholder }}
                        </div>
                        <p class="mt-1 text-xs text-neutral-500">
                            {{ format(currentTime, 'h:mm a') }}
                        </p>
                    </div>
                </div>

                <!-- Attached images and videos -->
                <div
                    v-for="(image, index) in images"
                    :key="`img-${index}-${image.url}`"
                    class="flex justify-start">
                    <div class="max-w-[85%] rounded-2xl rounded-bl-md bg-neutral-800 p-2">
                        <video
                            v-if="image.mimetype?.startsWith('video/')"
                            :src="image.url"
                            class="h-auto w-full rounded-lg"
                            controls
                            muted
                            playsinline
                            preload="metadata" />
                        <img
                            v-else
                            :src="image.url"
                            :alt="image.fileName"
                            class="h-auto w-full rounded-lg"
                            @error="handleImageError">
                    </div>
                </div>
            </div>

            <!-- Home indicator -->
            <div class="flex justify-center pb-2">
                <div class="h-1 w-28 rounded-full bg-neutral-700" />
            </div>
        </div>
    </div>
</template>
